import { __rest, __awaiter } from 'tslib';
import React, { useMemo } from 'react';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { PeekAutocomplete } from './PeekAutocomplete.js';
import { PeekInput } from './PeekInput.js';
import { PeekListItem } from './PeekListItem.js';

const PeekAddressInput = (_a) => {
    var _b, _c;
    var { field, form, type, searchFrom, searchFromRadius } = _a, props = __rest(_a, ["field", "form", "type", "searchFrom", "searchFromRadius"]);
    const requestOptions = useMemo(() => {
        const opts = {
            componentRestrictions: { country: ["us", "ca", "gb"] },
            types: type === null || type === void 0 ? void 0 : type.split(","),
        };
        if (searchFrom && searchFromRadius) {
            const circle = new window.google.maps.Circle({
                center: searchFrom,
                radius: searchFromRadius,
            });
            opts.locationBias = circle.getBounds();
        }
        return opts;
    }, [type, searchFrom, searchFromRadius]);
    const { ready, value, suggestions: { status, data }, setValue, clearSuggestions, } = usePlacesAutocomplete({
        requestOptions,
        debounce: 300,
    });
    const options = useMemo(() => data.map((p) => ({
        place_id: p.place_id,
        description: p.description,
        structured_formatting: p.structured_formatting,
    })), [data]);
    const handleSelect = (_, option) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!option)
            return;
        setValue(option.description, false);
        clearSuggestions();
        const results = yield getGeocode({ address: option.description });
        if (!((_b = (_a = results[0]) === null || _a === void 0 ? void 0 : _a.geometry) === null || _b === void 0 ? void 0 : _b.location))
            return;
        const { lat, lng } = yield getLatLng(results[0]);
        const comps = results[0].address_components || [];
        const getLong = (type) => { var _a; return ((_a = comps.find((c) => c.types.includes(type))) === null || _a === void 0 ? void 0 : _a.long_name) || ""; };
        const getShort = (type) => { var _a; return ((_a = comps.find((c) => c.types.includes(type))) === null || _a === void 0 ? void 0 : _a.short_name) || ""; };
        const name = getLong("premise") || option.structured_formatting.main_text;
        const city = getLong("locality") || getLong("sublocality") || getLong("administrative_area_level_2");
        const address = {
            name: name,
            description: `${name}, ${city}, ${getShort("administrative_area_level_1")} ${getLong("postal_code")}, ${getShort("country")}`,
            latitude: lat.toString(),
            longitude: lng.toString(),
            placeId: option.place_id,
            postalCode: getLong("postal_code"),
            city: city,
            state: getShort("administrative_area_level_1"),
            country: getShort("country"),
            geocodeData: results[0],
        };
        // @ts-ignore
        form.setFieldValue(field.name, address);
    });
    const handleInputChange = (_, newValue) => {
        setValue(newValue);
        // @ts-ignore
        form.setFieldValue(field.name, Object.assign(Object.assign({}, (field.value || {})), { name: newValue }));
    };
    const defaultValue = useMemo(
    // @ts-ignore
    () => { var _a, _b; return ({ description: ((_a = field.value) === null || _a === void 0 ? void 0 : _a.street2) || ((_b = field.value) === null || _b === void 0 ? void 0 : _b.name) || "" }); }, 
    // @ts-ignore
    [(_b = field.value) === null || _b === void 0 ? void 0 : _b.street2, (_c = field.value) === null || _c === void 0 ? void 0 : _c.name]);
    return (React.createElement(PeekAutocomplete, { freeSolo: false, popupIcon: null, disabled: !ready, noOptionsText: value.length >= 3 && status === "ZERO_RESULTS" ? "No address available" : "Search for an address", loading: value.length >= 3 && status !== "ZERO_RESULTS" && !!options.length, options: options, getOptionLabel: (opt) => opt.description, isOptionEqualToValue: (opt, val) => opt.place_id === val.place_id, defaultValue: defaultValue, onChange: handleSelect, onInputChange: handleInputChange, renderOption: (props, opt) => (React.createElement(PeekListItem, Object.assign({}, props, { textAlign: "left" }), opt.description)), sx: { ".MuiAutocomplete-input": { pl: "4px !important" } }, renderInput: (params) => (React.createElement(PeekInput, Object.assign({ field: Object.assign(Object.assign({}, field), { value, onChange: () => null }), form: form }, props, params))) }));
};

export { PeekAddressInput, PeekAddressInput as default };
